import Spinner from '@components/_elements/Spinner/Spinner';
import DeviceDataTable from '@components/_shared/DeviceDataTable/DeviceDataTable';
import UIBox from '@components/_shared/UIBox';
import IncidentCmdChart from '@components/IncidentCommand/IncidentCmdChart';
import IncidentMaxCellTMonitor from '@components/IncidentCommand/IncidentMaxCellTMonitor/IncidentMaxCellTMonitor';
import useGetInternalWidgetConfig from '@hooks/api/queries/widgets/useGetInternalWidgetConfig';
import useMakePageScrollable from '@hooks/useMakePageScrollable';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { SiteMeta } from '@src/types/SiteMeta';
import { WidgetTypeDeviceList } from '@src/types/Widget';
import storeConnector from '@store/storeConnector';
import { TimeMode } from '@types';
import { ReactElement, useMemo, useState } from 'react';

interface IncidentCommandProps {
  siteMeta: SiteMeta;
  timeMode: TimeMode;
}

function IncidentCommand(p: IncidentCommandProps): ReactElement {
  useMakePageScrollable();

  const { siteMeta, timeMode } = p;
  const [selectedUnits, setSelectedUnits] = useState<GridRowSelectionModel>([
    siteMeta.Units[0],
  ]);

  const rowSelectionModelChange = (
    rowSelectionModel: GridRowSelectionModel,
  ): void => {
    if (rowSelectionModel.length > 0) {
      //  save new selectedUnits to state
      setSelectedUnits(rowSelectionModel);
    }
  };

  const deviceNameDictionary = useMemo(
    () =>
      siteMeta.Units.map((unitId, i) => ({
        id: unitId,
        name: siteMeta.UnitNames[i],
      })),
    [siteMeta],
  );

  const {
    data: widgetConfig,
    isLoading,
    isError,
  } = useGetInternalWidgetConfig<WidgetTypeDeviceList>(
    'incidentCommandList',
    {},
  );

  const boxHeader = widgetConfig?.header || 'Incident Command List';

  const memoizedDevices = useMemo(
    () => widgetConfig?.devices || [],
    [widgetConfig?.devices],
  );

  const memoizedDataPoints = useMemo(
    () => widgetConfig?.dataPoints || [],
    [widgetConfig?.dataPoints],
  );

  const memoizedPointConfigRefs = useMemo(
    () => widgetConfig?.pointConfigRefs || [],
    [widgetConfig?.pointConfigRefs],
  );

  return isLoading ? (
    <Spinner type='fullPage' />
  ) : (
    <div className='home-row full-width'>
      <div className='fcol full-width'>
        <div className='frow full-width'>
          <div className='fcol full-width'>
            <UIBox header={boxHeader} isLoading={isLoading} isError={isError}>
              <DeviceDataTable
                widgetId='incident-command'
                enableCmdBtnColumn={false}
                hideFooter
                devices={memoizedDevices}
                dataPoints={memoizedDataPoints}
                pointConfigRefs={memoizedPointConfigRefs}
                useDualListView={widgetConfig?.useDualListView ?? false}
                rowSelectionModel={selectedUnits}
                checkboxSelection
                onRowSelectionChange={rowSelectionModelChange}
                deviceNameDictionary={deviceNameDictionary}
                getRowClassName={() => 'MuiDataGrid-clickable'}
                boxStyle={{ height: '400px' }}
              />
            </UIBox>
          </div>
        </div>
        <div className='frow full-width'>
          <div className='fcol full-width'>
            <UIBox header='Max Ambient Temperature'>
              <IncidentCmdChart selectedUnits={selectedUnits} />
            </UIBox>
          </div>
        </div>
        <div className='frow full-width'>
          <div className='fcol full-width'>
            <UIBox header='High Temperatures Racks Monitor'>
              <IncidentMaxCellTMonitor
                selectedUnits={selectedUnits}
                siteMeta={siteMeta}
                timeMode={timeMode}
                widgetConfig={widgetConfig}
              />
            </UIBox>
          </div>
        </div>
      </div>
    </div>
  );
}

export default storeConnector(IncidentCommand, {
  config: ['siteMeta'],
  service: ['timeMode'],
});
